import {Async, Loaded} from 'Core/models/Async';
import {Email, UnvalidatedEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {UnvalidatedPassword} from 'Core/models/Password';
import {EmptyPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {UnvalidatedSecurityQuestion} from 'Core/models/SecurityQuestion';
import {Either, left} from 'fp-ts/lib/Either';
import {ReducerState} from 'rootReducer';
import {createAction} from 'Shared/utilities/actionCreator';
import {InvalidUserRegistration, UserRegistration, ValidUserRegistration} from 'Users/models/UserRegistration';

export enum RegistrationActionType {
  changeUserRegistration = 'registration/CHANGE_USER_REGISTRATION',
  register = 'registration/REGISTER',
  upload = 'registration/UPLOAD',
  VALIDATE_USERNAME = 'registration/VALIDATE_USERNAME',
  SET_USERNAME = 'registration/SET_USERNAME',
  VALIDATE_PHONE_NUMBER = 'registration/VALIDATE_PHONE_NUMBER',
  SET_PHONE_NUMBER = 'registration/SET_PHONE_NUMBER',
  VALIDATE_EMAIL = 'registration/VALIDATE_EMAIL',
  SET_EMAIL = 'registration/SET_EMAIL',
}

export const validateEmail = (email: string, registrationKey: string) =>
  createAction(RegistrationActionType.VALIDATE_EMAIL, {email, registrationKey});
export const setEmail = (email: Async<Either<Email, ValidEmail>>) =>
  createAction(RegistrationActionType.SET_EMAIL, {email});
export const validatePhoneNumber = (phoneNumber: string, registrationKey: string) =>
  createAction(RegistrationActionType.VALIDATE_PHONE_NUMBER, {phoneNumber, registrationKey});
export const setPhoneNumber = (phoneNumber: Async<Either<PhoneNumber, ValidPhoneNumber>>) =>
  createAction(RegistrationActionType.SET_PHONE_NUMBER, {phoneNumber});
export const validateUsername = (username: string, registrationKey: string) =>
  createAction(RegistrationActionType.VALIDATE_USERNAME, {username, registrationKey});
export const setUsername = (username: Async<Either<StringUnion, NonEmptyString>>) =>
  createAction(RegistrationActionType.SET_USERNAME, {username});
export const clearUserRegistration = () =>
  createAction(RegistrationActionType.changeUserRegistration, DEFAULT_STATE.userRegistration);
export const showLoadingProgress = (registration: UserRegistration) =>
  createAction(RegistrationActionType.changeUserRegistration, registration);
export const changeUserRegistration = (registration: UserRegistration) =>
  createAction(
    RegistrationActionType.changeUserRegistration,
    ValidUserRegistration.create(
      registration.email,
      registration.userName,
      registration.phoneNumber,
      registration.password,
      registration.securityQuestionOne,
      registration.securityQuestionTwo
    )
  );

export const registerUser = (
  firstName: string,
  lastName: string,
  userKey: string,
  registration: ValidUserRegistration
) =>
  createAction(RegistrationActionType.register, {
    firstName,
    lastName,
    userKey,
    registration,
  });

export const DEFAULT_STATE: RegistrationState = {
  userRegistration: new InvalidUserRegistration(
    new Loaded(left(new UnvalidatedEmail(''))),
    new Loaded(left(new EmptyString())),
    new Loaded(left(new EmptyPhoneNumber())),
    new UnvalidatedPassword('', ''),
    new UnvalidatedSecurityQuestion('', ''),
    new UnvalidatedSecurityQuestion('', '')
  ),
};

type RegistrationState = {
  userRegistration: UserRegistration;
};
type RegistrationAction =
  | ReturnType<typeof showLoadingProgress>
  | ReturnType<typeof changeUserRegistration>
  | ReturnType<typeof setPhoneNumber>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof setUsername>;

export default (state: RegistrationState = DEFAULT_STATE, action: RegistrationAction): RegistrationState => {
  switch (action.type) {
    case RegistrationActionType.SET_EMAIL: {
      const newRegistration = ValidUserRegistration.create(
        action.payload.email,
        state.userRegistration.userName,
        state.userRegistration.phoneNumber,
        state.userRegistration.password,
        state.userRegistration.securityQuestionOne,
        state.userRegistration.securityQuestionTwo
      );

      return {
        ...state,
        userRegistration: newRegistration,
      };
    }
    case RegistrationActionType.SET_PHONE_NUMBER: {
      const newRegistration = ValidUserRegistration.create(
        state.userRegistration.email,
        state.userRegistration.userName,
        action.payload.phoneNumber,
        state.userRegistration.password,
        state.userRegistration.securityQuestionOne,
        state.userRegistration.securityQuestionTwo
      );
      return {
        ...state,
        userRegistration: newRegistration,
      };
    }
    case RegistrationActionType.SET_USERNAME: {
      const newRegistration = ValidUserRegistration.create(
        state.userRegistration.email,
        action.payload.username,
        state.userRegistration.phoneNumber,
        state.userRegistration.password,
        state.userRegistration.securityQuestionOne,
        state.userRegistration.securityQuestionTwo
      );
      return {
        ...state,
        userRegistration: newRegistration,
      };
    }
    case RegistrationActionType.changeUserRegistration:
      return {
        ...state,
        userRegistration: action.payload,
      };
    default:
      return state;
  }
};

export const getUserRegistration = (state: ReducerState) => state.user.registration.userRegistration;
